/** Tab „Mein Konto": Kontodaten, Passwort, Discord-Verknüpfung, Sitzungen, Privatsphäre, Konto löschen. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FIXED_LIMITS, type DmPolicy, type SessionDto } from '@lagerfeuer/shared';
import { Monitor, Smartphone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmDialog } from '../../components/Modal';
import { ApiError, errorMessage, trackOwnPasswordChange } from '../../lib/api';
import { authApi, usersApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { paths } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { clearLocalSession, logout } from '../auth/session';
import { useAuthConfig } from '../auth/useAuthConfig';
import { applyMe, patchSettings } from './useSettings';

export function AccountTab() {
  const me = useAppStore((s) => s.me);
  if (!me) return null;
  return (
    <div className="lf-settings-sections">
      <AccountFields key={me.id} />
      <section className="lf-settings-card" aria-labelledby="pw-heading">
        <h2 id="pw-heading">{me.hasPassword ? 'Passwort ändern' : 'Passwort festlegen'}</h2>
        <PasswordForm hasPassword={me.hasPassword} />
      </section>
      <DiscordSection />
      <PrivacySection />
      <SessionsSection />
      <DangerZone />
    </div>
  );
}

const USERNAME_RE = /^[a-z0-9_.]{2,32}$/;

function AccountFields() {
  const me = useAppStore((s) => s.me)!;
  const [username, setUsername] = useState(me.username);
  const [displayName, setDisplayName] = useState(me.displayName ?? '');
  const [email, setEmail] = useState(me.email ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = username !== me.username || displayName !== (me.displayName ?? '') || email !== (me.email ?? '');
  const usernameValid = USERNAME_RE.test(username);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!usernameValid) return;
    setBusy(true);
    setError(null);
    try {
      const patch: Parameters<typeof usersApi.updateMe>[0] = {};
      if (username !== me.username) patch.username = username;
      if (displayName !== (me.displayName ?? '')) patch.displayName = displayName.trim() || null;
      if (email !== (me.email ?? '')) patch.email = email.trim() || null;
      applyMe(await usersApi.updateMe(patch));
      toast.success('Konto gespeichert');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'USERNAME_TAKEN') setError('Dieser Benutzername ist bereits vergeben.');
      else if (err instanceof ApiError && err.code === 'EMAIL_TAKEN') setError('Diese E-Mail-Adresse wird bereits verwendet.');
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="lf-settings-card stack" onSubmit={submit} aria-labelledby="account-heading" data-testid="account-form">
      <h2 id="account-heading">Kontodaten</h2>
      <div className="field">
        <label htmlFor="acc-username">Benutzername</label>
        <input
          id="acc-username"
          className="input"
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase())}
          maxLength={FIXED_LIMITS.usernameMax}
          aria-invalid={!usernameValid}
          autoComplete="username"
          data-testid="account-username"
        />
        <span className="field-hint">2–32 Zeichen: Kleinbuchstaben, Ziffern, Punkt und Unterstrich.</span>
      </div>
      <div className="field">
        <label htmlFor="acc-display">Anzeigename</label>
        <input
          id="acc-display"
          className="input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={FIXED_LIMITS.displayNameMax}
          placeholder={me.username}
          data-testid="account-display-name"
        />
      </div>
      <div className="field">
        <label htmlFor="acc-email">E-Mail</label>
        <input id="acc-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="account-email" />
      </div>
      {error && (
        <p className="error-text" role="alert" data-testid="account-error">
          {error}
        </p>
      )}
      <div className="row">
        <span className="spacer" />
        {dirty && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setUsername(me.username);
              setDisplayName(me.displayName ?? '');
              setEmail(me.email ?? '');
            }}
            data-testid="account-reset"
          >
            Zurücksetzen
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={!dirty || busy || !usernameValid} data-testid="account-save">
          Speichern
        </button>
      </div>
    </form>
  );
}

/** Passwort ändern (aktuelles Passwort Pflicht, wenn eins gesetzt ist). */
export function PasswordForm({ hasPassword, onDone }: { hasPassword: boolean; onDone?: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < FIXED_LIMITS.passwordMin;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = (!hasPassword || current.length > 0) && next.length >= FIXED_LIMITS.passwordMin && next === confirm && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await trackOwnPasswordChange(authApi.changePassword(hasPassword ? { currentPassword: current, newPassword: next } : { newPassword: next }));
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
      toast.success('Passwort geändert', 'Andere Sitzungen wurden abgemeldet.');
      onDone?.();
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'INVALID_CREDENTIALS' || err.status === 401 || err.status === 403)) setError('Das aktuelle Passwort ist falsch.');
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit} data-testid="password-form" noValidate>
      {hasPassword && (
        <div className="field">
          <label htmlFor="pw-current">Aktuelles Passwort</label>
          <input id="pw-current" className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" data-testid="password-current" />
        </div>
      )}
      <div className="field">
        <label htmlFor="pw-new">Neues Passwort</label>
        <input
          id="pw-new"
          className="input"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          maxLength={FIXED_LIMITS.passwordMax}
          aria-invalid={tooShort}
          aria-describedby="pw-new-hint"
          data-testid="password-new"
        />
        <span id="pw-new-hint" className={tooShort ? 'error-text' : 'field-hint'}>
          Mindestens {FIXED_LIMITS.passwordMin} Zeichen.
        </span>
      </div>
      <div className="field">
        <label htmlFor="pw-confirm">Neues Passwort bestätigen</label>
        <input
          id="pw-confirm"
          className="input"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          aria-invalid={mismatch}
          data-testid="password-confirm"
        />
        {mismatch && (
          <span className="error-text" data-testid="password-mismatch">
            Die Passwörter stimmen nicht überein.
          </span>
        )}
      </div>
      {error && (
        <p className="error-text" role="alert" data-testid="password-error">
          {error}
        </p>
      )}
      {done && (
        <p className="small" role="status" data-testid="password-done">
          Passwort wurde geändert.
        </p>
      )}
      <div className="row">
        <span className="spacer" />
        <button type="submit" className="btn btn-primary" disabled={!canSubmit} data-testid="password-submit">
          {hasPassword ? 'Passwort ändern' : 'Passwort festlegen'}
        </button>
      </div>
    </form>
  );
}

function DiscordSection() {
  const config = useAuthConfig();
  const me = useAppStore((s) => s.me)!;
  const [busy, setBusy] = useState(false);
  if (!config.discordLogin && !me.discord) return null;
  const link = async () => {
    setBusy(true);
    try {
      const { url } = await authApi.discordLinkUrl();
      window.location.assign(url);
    } catch (e) {
      toast.error('Verknüpfung nicht möglich', errorMessage(e));
      setBusy(false);
    }
  };
  const unlink = async () => {
    setBusy(true);
    try {
      await authApi.discordUnlink();
      applyMe({ ...me, discord: null });
      toast.success('Verknüpfung gelöst');
    } catch (e) {
      toast.error('Verknüpfung konnte nicht gelöst werden', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="lf-settings-card stack" aria-labelledby="discord-heading" data-testid="discord-section">
      <h2 id="discord-heading">Verknüpftes Discord-Konto</h2>
      {me.discord ? (
        <div className="row">
          <span>
            Verknüpft mit <strong data-testid="discord-linked-name">{me.discord.username}</strong>
          </span>
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={() => void unlink()} disabled={busy || !me.hasPassword} data-testid="discord-unlink">
            Verknüpfung lösen
          </button>
        </div>
      ) : (
        <div className="row">
          <span className="muted">Melde dich zusätzlich mit deinem Discord-Konto an.</span>
          <span className="spacer" />
          <button className="btn btn-primary" onClick={() => void link()} disabled={busy} data-testid="discord-link">
            Verknüpfen
          </button>
        </div>
      )}
      {me.discord && !me.hasPassword && <p className="field-hint">Lege zuerst ein Passwort fest, bevor du die Verknüpfung löst.</p>}
    </section>
  );
}

const DM_POLICY_LABELS: Record<DmPolicy, string> = {
  EVERYONE: 'Alle',
  SERVER_MEMBERS: 'Nur Mitglieder gemeinsamer Server',
  NOBODY: 'Niemand',
};

function PrivacySection() {
  const policy = useAppStore((s) => s.settings?.dmPolicy ?? 'EVERYONE');
  return (
    <section className="lf-settings-card stack" aria-labelledby="privacy-heading">
      <h2 id="privacy-heading">Privatsphäre</h2>
      <div className="field">
        <label htmlFor="dm-policy">Direktnachrichten erlauben von</label>
        <select id="dm-policy" className="select" value={policy} onChange={(e) => void patchSettings({ dmPolicy: e.target.value as DmPolicy })} data-testid="dm-policy">
          {(Object.keys(DM_POLICY_LABELS) as DmPolicy[]).map((p) => (
            <option key={p} value={p}>
              {DM_POLICY_LABELS[p]}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}

function deviceLabel(ua: string | null): { label: string; mobile: boolean } {
  if (!ua) return { label: 'Unbekanntes Gerät', mobile: false };
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const browser = /Firefox\//.test(ua) ? 'Firefox' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad|iOS/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return { label: os ? `${browser} auf ${os}` : browser, mobile };
}

const fmtDate = (iso: string) => new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

function SessionsSection() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const sessions = useQuery({ queryKey: qk.sessions, queryFn: authApi.sessions });
  const [confirmAll, setConfirmAll] = useState(false);
  const revoke = async (s: SessionDto) => {
    try {
      await authApi.revokeSession(s.id);
      qc.setQueryData<SessionDto[]>(qk.sessions, (old) => old?.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error('Sitzung konnte nicht beendet werden', errorMessage(e));
    }
  };
  return (
    <section className="lf-settings-card stack" aria-labelledby="sessions-heading" data-testid="sessions-section">
      <h2 id="sessions-heading">Angemeldete Geräte</h2>
      {sessions.isLoading && <div className="spinner" aria-label="Lädt" />}
      {sessions.isError && <p className="error-text">{errorMessage(sessions.error)}</p>}
      <ul className="lf-session-list">
        {[...(sessions.data ?? [])]
          .sort((a, b) => Number(b.current) - Number(a.current) || b.lastUsedAt.localeCompare(a.lastUsedAt))
          .map((s) => {
            const d = deviceLabel(s.userAgent);
            return (
              <li key={s.id} className="lf-session" data-testid="session-item" data-current={s.current || undefined}>
                {d.mobile ? <Smartphone size={22} aria-hidden="true" /> : <Monitor size={22} aria-hidden="true" />}
                <div className="lf-session-text">
                  <div>
                    {d.label} {s.current && <span className="tag">Dieses Gerät</span>}
                  </div>
                  <div className="small faint">
                    {s.ip ? `${s.ip} · ` : ''}zuletzt aktiv {fmtDate(s.lastUsedAt)}
                  </div>
                </div>
                {!s.current && (
                  <button className="btn btn-sm btn-ghost" onClick={() => void revoke(s)} data-testid="session-revoke" aria-label={`Sitzung ${d.label} abmelden`}>
                    Abmelden
                  </button>
                )}
              </li>
            );
          })}
      </ul>
      <div className="row">
        <span className="spacer" />
        <button className="btn btn-danger" onClick={() => setConfirmAll(true)} data-testid="logout-all">
          Überall abmelden
        </button>
      </div>
      {confirmAll && (
        <ConfirmDialog
          title="Überall abmelden?"
          message="Alle Sitzungen auf allen Geräten werden beendet – auch diese."
          confirmLabel="Überall abmelden"
          danger
          onClose={() => setConfirmAll(false)}
          onConfirm={() => void logout(true).then(() => navigate(paths.login()))}
        />
      )}
    </section>
  );
}

function DangerZone() {
  const me = useAppStore((s) => s.me)!;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = confirmText === me.username && (!me.hasPassword || password.length > 0);
  const remove = async () => {
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await usersApi.deleteMe(me.hasPassword ? password : undefined);
      clearLocalSession();
      void navigate(paths.login());
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <section className="lf-settings-card lf-danger-zone stack" aria-labelledby="danger-heading">
      <h2 id="danger-heading">Konto löschen</h2>
      <p className="muted small">Dein Konto und deine Daten werden endgültig gelöscht. Server, die du besitzt, musst du vorher übertragen oder löschen.</p>
      <div className="row">
        <span className="spacer" />
        <button className="btn btn-danger" onClick={() => setOpen(true)} data-testid="delete-account">
          Konto löschen
        </button>
      </div>
      {open && (
        <ConfirmDialog
          title="Konto endgültig löschen?"
          message="Das kann nicht rückgängig gemacht werden."
          confirmLabel="Konto löschen"
          danger
          busy={busy || !ok}
          onClose={() => setOpen(false)}
          onConfirm={() => void remove()}
        >
          <div className="field">
            <label htmlFor="del-confirm">Gib zur Bestätigung deinen Benutzernamen ein: {me.username}</label>
            <input id="del-confirm" className="input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-account-confirm" />
          </div>
          {me.hasPassword && (
            <div className="field">
              <label htmlFor="del-pw">Passwort</label>
              <input id="del-pw" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="delete-account-password" />
            </div>
          )}
          {error && (
            <p className="error-text" role="alert" data-testid="delete-account-error">
              {error}
            </p>
          )}
        </ConfirmDialog>
      )}
    </section>
  );
}
